import { useCatalogSearch } from '~/features/catalog/routes/resources/CatalogSearchRoute';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import {
  QUERY_SEARCH_PARAM_CATALOG_AGRID,
  QUERY_SEARCH_PARAM_CATALOG_HAGRID
} from '~/features/catalog/mappers/catalogSearchQueryMapper';
import { cn } from '~/common/utils/styleUtil';
import type { ICatalogItemDto } from '~/dtos/hub/hub.models.generated';
import { useBasePath } from '~/common/hooks/useBasePath';

const normalizeText = (maxLength: number, text: string, count?: number) => {
  const newText = !text ? '' : text.replaceAll('/', ' / ');
  const countText = count !== undefined ? ` (${count})` : '';

  const combinedText = newText + countText;

  if (maxLength && combinedText.length > maxLength) {
    const truncatedText = newText.slice(0, maxLength - countText.length - 3) + '...';
    return (
      <>
        {truncatedText}
        <span style={{ whiteSpace: 'nowrap' }}>{countText}</span>
      </>
    );
  }

  return (
    <>
      {newText}
      <span style={{ whiteSpace: 'nowrap' }}>{countText}</span>
    </>
  );
};

export interface CategorySearchResultsProps {
  className?: string;
  onSearchCategorySelected?: () => void;
}

export function CategorySearchResults({ className, onSearchCategorySelected }: Readonly<CategorySearchResultsProps>) {
  const { t } = useTranslation();
  const { catalogItems } = useCatalogSearch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { path } = useBasePath();

  function handleSearchCatalogHagr(cat: ICatalogItemDto) {
    onSearchCategorySelected?.();

    const urlParams = new URLSearchParams(searchParams.toString());
    urlParams.set(QUERY_SEARCH_PARAM_CATALOG_HAGRID, cat.id.toString());
    urlParams.delete(QUERY_SEARCH_PARAM_CATALOG_AGRID);

    navigate(`${path}/catalog?${urlParams.toString()}`);
  }

  function handleSearchCatalogAgr(cat: ICatalogItemDto) {
    onSearchCategorySelected?.();

    const urlParams = new URLSearchParams(searchParams.toString());
    urlParams.set(QUERY_SEARCH_PARAM_CATALOG_AGRID, cat.id.toString());
    urlParams.delete(QUERY_SEARCH_PARAM_CATALOG_HAGRID);

    navigate(`${path}/catalog?${urlParams.toString()}`);
  }

  const hagrId = searchParams.has(QUERY_SEARCH_PARAM_CATALOG_HAGRID)
    ? searchParams.get(QUERY_SEARCH_PARAM_CATALOG_HAGRID)
    : undefined;
  const agrId = searchParams.has(QUERY_SEARCH_PARAM_CATALOG_AGRID)
    ? searchParams.get(QUERY_SEARCH_PARAM_CATALOG_AGRID)
    : undefined;

  return (
    <div className={cn(className)}>
      <div className={'pl-0 font-bold text-xl mb-4'}>{t('Suchergebnisse')}</div>
      {catalogItems.length === 0 && <div className="pl-0">{t('Keine Ergebnisse gefunden')}</div>}
      {catalogItems.map(x => {
        return (
          <div key={x.id + x.name}>
            <button type="button"
              className={'border-b w-full text-start py-2 pl-2 pr-2 hover:text-primary'}
              onClick={() => handleSearchCatalogHagr(x)}
            >
              <div className={cn('', hagrId === x.id.toString() && 'text-primary')}>
                {normalizeText(200, x.name, x.count)}
              </div>
            </button>
            {x.child &&
              [...x.child]
                .sort((a, b) => b.count - a.count)
                .map(c => {
                  return (
                    <button type="button"
                      className={'border-b w-full text-start py-2 pl-6 p-r-2 hover:text-primary'}
                      key={c.id + c.name}
                      onClick={() => handleSearchCatalogAgr(c)}
                    >
                      <div className={cn('', agrId === c.id.toString() && 'text-primary')}>
                        {normalizeText(200, c.name, c.count)}
                      </div>
                    </button>
                  );
                })}
          </div>
        );
      })}
    </div>
  );
}
